import { useCallback, useEffect, useRef, useState } from "react";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useModal } from "../../hooks/useModal";
import {
  clearActiveFocusSessionSummary,
  getLiveFocusSessionSnapshot,
  normalizeActiveFocusSessionSummary,
  readActiveFocusSessionSummary,
  storeActiveFocusSessionSummary,
} from "../../utils/timeTracking";

const pomodoroStages = [
  { label: "Odak 1", type: "focus", duration: 1500 },
  { label: "Kısa Mola 1", type: "short-break", duration: 300 },
  { label: "Odak 2", type: "focus", duration: 1500 },
  { label: "Kısa Mola 2", type: "short-break", duration: 300 },
  { label: "Odak 3", type: "focus", duration: 1500 },
  { label: "Kısa Mola 3", type: "short-break", duration: 300 },
  { label: "Odak 4", type: "focus", duration: 1500 },
  { label: "Uzun Mola", type: "long-break", duration: 900 },
];

const playNotificationSound = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(660, audioContext.currentTime + 0.4);
    gain.gain.setValueAtTime(0.001, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, audioContext.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.5);
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.5);
    oscillator.onended = () => {
      void audioContext.close().catch(() => {});
    };
  } catch {}
};

export default function FocusSessionController() {
  const { isAuthenticated, user } = useAuth();
  const { showAlert, showToast } = useModal();
  const [activeSummary, setActiveSummary] = useState<any>(() => readActiveFocusSessionSummary());
  const [, setTick] = useState(0);
  const transitionLockRef = useRef(false);

  const liveSession = getLiveFocusSessionSnapshot(activeSummary);

  const syncSummary = useCallback((session: any, terminal = false) => {
    const normalized = terminal ? null : normalizeActiveFocusSessionSummary(session);
    setActiveSummary(normalized);
    if (normalized) {
      storeActiveFocusSessionSummary(normalized);
    } else {
      clearActiveFocusSessionSummary();
    }
  }, []);

  const notify = useCallback(
    (message: string, modalTitle?: string) => {
      playNotificationSound();
      showToast(message, "success");
      if (modalTitle) {
        void showAlert({
          title: modalTitle,
          message,
          type: "success",
          buttonText: "Tamam",
        });
      }
    },
    [showAlert, showToast]
  );

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setActiveSummary(null);
      return;
    }
    const interval = window.setInterval(() => {
      setTick((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      setActiveSummary(normalizeActiveFocusSessionSummary(customEvent.detail) || null);
    };
    window.addEventListener("activeFocusSessionChanged", handler);
    return () => window.removeEventListener("activeFocusSessionChanged", handler);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !liveSession || transitionLockRef.current) return;
    if (liveSession.type === "stopwatch" || liveSession.status !== "running" || (liveSession.liveRemainingSeconds ?? 1) > 0) return;

    const completeFocusFlow = async () => {
      transitionLockRef.current = true;
      try {
        if (liveSession.type === "timer") {
          notify("Zamanlayıcı oturumu tamamlandı.", "Zamanlayıcı Tamamlandı");
          const response = await ApiService.recordFocusEvent(liveSession.id, {
            eventType: "completed",
            elapsedSeconds: liveSession.liveElapsedSeconds,
            remainingSeconds: 0,
            status: "completed",
            metadata: liveSession.metadata || {},
          });
          syncSummary(response, true);
          window.dispatchEvent(new CustomEvent("focusTrackingDataChanged"));
          return;
        }

        const currentPhaseIndex = Number(liveSession.metadata?.currentPhaseIndex || 0);
        const currentPhaseLabel = liveSession.metadata?.currentPhaseLabel || `Faz ${currentPhaseIndex + 1}`;
        const nextStage = pomodoroStages[currentPhaseIndex + 1];

        await ApiService.recordFocusEvent(liveSession.id, {
          eventType: "phase_completed",
          elapsedSeconds: liveSession.liveElapsedSeconds,
          remainingSeconds: 0,
          status: "running",
          metadata: { ...liveSession.metadata, completedPhaseIndex: currentPhaseIndex },
        });

        if (!nextStage) {
          notify("Pomodoro döngüsü tamamlandı.", "Pomodoro Tamamlandı");
          const response = await ApiService.recordFocusEvent(liveSession.id, {
            eventType: "completed",
            elapsedSeconds: liveSession.liveElapsedSeconds,
            remainingSeconds: 0,
            status: "completed",
            metadata: liveSession.metadata || {},
          });
          syncSummary(response, true);
          window.dispatchEvent(new CustomEvent("focusTrackingDataChanged"));
          return;
        }

        const response = await ApiService.recordFocusEvent(liveSession.id, {
          eventType: "phase_started",
          elapsedSeconds: liveSession.liveElapsedSeconds,
          remainingSeconds: nextStage.duration,
          status: "running",
          metadata: {
            ...liveSession.metadata,
            stages: pomodoroStages,
            currentPhaseIndex: currentPhaseIndex + 1,
            currentPhaseLabel: nextStage.label,
            currentPhaseType: nextStage.type,
            currentPhaseDurationSeconds: nextStage.duration,
          },
        });

        notify(`${currentPhaseLabel} tamamlandı. ${nextStage.label} başladı.`);
        syncSummary(response);
        window.dispatchEvent(new CustomEvent("focusTrackingDataChanged"));
      } catch {} finally {
        transitionLockRef.current = false;
      }
    };

    void completeFocusFlow();
  }, [isAuthenticated, liveSession, notify, syncSummary, user?.id]);

  return null;
}
